import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard,
  Users,
  Truck,
  MapPin,
  Calendar,
  CheckSquare,
  AlertTriangle,
  FileWarning,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Shield,
  Crown,
  Cat,
  Star,
  Heart,
  Rocket,
  Dog,
  Bird,
  Fish,
  Flame,
  Zap,
  Coffee,
  Music,
  Gamepad2,
  Trophy,
  Diamond,
  Gift,
  Sparkles,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  Search,
  RefreshCw,
  Wrench,
  Clock,
  Database,
  Wifi,
  WifiOff,
  UserPlus
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { GlobalSearch, SearchButton } from './GlobalSearch';
import { useRealTime } from '../context/RealTimeContext';
import { OfflineBanner } from './OfflineBanner';

// Icon mapping for profile
const PROFILE_ICONS = {
  crown: { icon: Crown, color: 'text-yellow-400' },
  cat: { icon: Cat, color: 'text-orange-400' },
  star: { icon: Star, color: 'text-yellow-300' },
  heart: { icon: Heart, color: 'text-pink-400' },
  rocket: { icon: Rocket, color: 'text-blue-400' },
  shield: { icon: Shield, color: 'text-cyan-400' },
  dog: { icon: Dog, color: 'text-amber-400' },
  bird: { icon: Bird, color: 'text-sky-400' },
  fish: { icon: Fish, color: 'text-teal-400' },
  flame: { icon: Flame, color: 'text-orange-500' },
  zap: { icon: Zap, color: 'text-yellow-400' },
  coffee: { icon: Coffee, color: 'text-amber-600' },
  music: { icon: Music, color: 'text-purple-400' },
  gamepad: { icon: Gamepad2, color: 'text-green-400' },
  trophy: { icon: Trophy, color: 'text-yellow-500' },
  diamond: { icon: Diamond, color: 'text-cyan-300' },
  gift: { icon: Gift, color: 'text-red-400' },
  sparkles: { icon: Sparkles, color: 'text-purple-300' },
};

// Profile Avatar Component
const ProfileAvatar = ({ user, profileIcon, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  };
  
  const iconSizes = { sm: 16, md: 20, lg: 24 };
  
  if (profileIcon && PROFILE_ICONS[profileIcon]) {
    const IconComponent = PROFILE_ICONS[profileIcon].icon;
    const iconColor = PROFILE_ICONS[profileIcon].color;
    return (
      <div className={cn(
        'rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg',
        sizeClasses[size]
      )}>
        <IconComponent size={iconSizes[size]} className={iconColor} />
      </div>
    );
  }
  
  return (
    <div className={cn(
      'rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center font-bold text-white shadow-lg',
      sizeClasses[size]
    )}>
      {user?.prenom?.[0]}{user?.nom?.[0]}
    </div>
  );
};

// Theme Toggle Button
const ThemeToggle = ({ className }) => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={cn("theme-toggle", className)}
      title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
      data-testid="theme-toggle"
    >
      {theme === 'dark' ? (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-yellow-400/20 flex items-center justify-center">
            <Zap size={16} className="text-yellow-400" />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
            <Sparkles size={16} className="text-indigo-500" />
          </div>
        </div>
      )}
    </button>
  );
};

// Real-time WebSocket Connection Indicator
const RealTimeIndicator = () => {
  const { isConnected } = useRealTime();
  const { theme } = useTheme();
  const { isOnline } = useAuth();
  
  // Use actual network status
  const effectivelyConnected = isOnline && navigator.onLine;
  
  return (
    <div 
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all",
        effectivelyConnected
          ? (theme === 'dark' 
              ? "bg-green-500/20 text-green-400" 
              : "bg-green-100 text-green-700")
          : (theme === 'dark'
              ? "bg-zinc-700 text-zinc-400"
              : "bg-gray-200 text-gray-500")
      )}
      title={effectivelyConnected ? "Synchronisation active" : "Hors ligne - Données en cache"}
    >
      <Wifi size={12} className={!effectivelyConnected ? "opacity-50" : ""} />
      <span className="hidden sm:inline">{effectivelyConnected ? 'Live' : 'Hors ligne'}</span>
    </div>
  );
};

// Mobile Drawer Menu
const MobileDrawer = ({ isOpen, onClose, navigation, user, logout, profileIcon, unreadMessages, expandedMenus, toggleMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const handleNavClick = (href) => {
    navigate(href);
    onClose();
  };

  const isItemActive = (item) => {
    if (item.href) return location.pathname === item.href;
    if (item.children) {
      return item.children.some(child => location.pathname === child.href);
    }
    return false;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 md:hidden"
        onClick={onClose}
        data-testid="mobile-drawer-overlay"
      />
      
      {/* Drawer */}
      <div 
        className={cn(
          'fixed top-0 left-0 h-full w-[280px] z-50 md:hidden flex flex-col shadow-2xl',
          'transform transition-transform duration-300 ease-out',
          theme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-white',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )} 
        style={{ 
          paddingTop: 'max(calc(env(safe-area-inset-top) * 0.3), 4px)',
          paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
          paddingLeft: 'env(safe-area-inset-left)'
        }}
        data-testid="mobile-drawer"
      >
        
        {/* Header - Logo centré */}
        <div className={cn(
          'px-2 pt-6 flex items-center justify-center relative border-b',
          theme === 'dark' ? 'border-white/10' : 'border-gray-200'
        )}>
          {/* Logo centré */}
          <img 
            src={theme === 'dark' ? '/logo-white.png?v=2' : '/logo.png?v=2'}
            alt="BY TRANSPORT" 
            className="h-32 w-auto"
          />
          
          {/* Bouton fermer en position absolue à droite */}
          <button
            onClick={onClose}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-lg transition-colors',
              theme === 'dark' ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-700'
            )}
            data-testid="close-drawer-button"
          >
            <X size={24} />
          </button>
        </div>

        {/* User Info */}
        {user && (
          <div className={cn(
            'px-4 py-1 border-b',
            theme === 'dark' ? 'border-white/10' : 'border-gray-200'
          )}>
            <div className="flex items-center justify-center gap-3">
              <ProfileAvatar user={user} profileIcon={profileIcon} size="sm" />
              <div className="flex-1 min-w-0 text-center">
                <div className={cn(
                  'font-semibold truncate text-sm',
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                )}>
                  {user.prenom} {user.nom}
                </div>
                <div className={cn(
                  'text-xs capitalize',
                  theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
                )}>
                  {user.role}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <div className="space-y-1">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const isActive = isItemActive(item);
              const isExpanded = expandedMenus[item.name];
              const showBadge = item.href === '/messages' && unreadMessages > 0;
              
              // Item with sub-menu
              if (item.isGroup && item.children) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={cn(
                        'w-full flex items-center justify-start gap-3 px-4 py-3 rounded-xl transition-all',
                        isActive
                          ? theme === 'dark'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-red-50 text-red-600'
                          : theme === 'dark'
                            ? 'text-zinc-300 hover:bg-white/5 hover:text-white'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      )}
                    >
                      <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
                      <span className="flex-1 text-left font-medium">{item.name}</span>
                      <ChevronDown 
                        size={16} 
                        className={cn(
                          'transition-transform duration-200',
                          isExpanded ? 'rotate-180' : ''
                        )} 
                      />
                    </button>
                    
                    {/* Sub-menu items */}
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-white/10 pl-2">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = location.pathname === child.href;
                          const childBadge = child.href === '/messages' && unreadMessages > 0;
                          
                          return (
                            <button
                              key={child.href}
                              onClick={() => handleNavClick(child.href)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm',
                                childActive 
                                  ? theme === 'dark'
                                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                    : 'bg-red-50 text-red-600 border border-red-200'
                                  : theme === 'dark'
                                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                              )}
                            >
                              <ChildIcon size={16} strokeWidth={childActive ? 2 : 1.5} />
                              <span className="flex-1 text-left">{child.name}</span>
                              {childBadge && (
                                <span className="w-5 h-5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                  {unreadMessages > 9 ? '9+' : unreadMessages}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular item without sub-menu
              return (
                <button
                  key={item.href || index}
                  onClick={() => handleNavClick(item.href)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all',
                    isActive 
                      ? theme === 'dark'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-red-50 text-red-600 border border-red-200'
                      : theme === 'dark'
                        ? 'text-zinc-300 hover:bg-white/5 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  )}
                  data-testid={`mobile-nav-${item.href?.slice(1)}`}
                >
                  <div className="relative">
                    <Icon size={20} strokeWidth={isActive ? 2.5 : 1.5} />
                    {showBadge && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </div>
                  <span className="flex-1 text-left font-medium">{item.name}</span>
                  {isActive && <ChevronRight size={16} />}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className={cn(
          'p-4 border-t space-y-3',
          theme === 'dark' ? 'border-white/10' : 'border-gray-200'
        )}>
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors',
              theme === 'dark' 
                ? 'text-zinc-300 hover:bg-white/5' 
                : 'text-gray-700 hover:bg-gray-100'
            )}
            data-testid="mobile-theme-toggle"
          >
            {theme === 'dark' ? (
              <>
                <Zap size={20} className="text-yellow-400" />
                <span className="flex-1 text-left font-medium">Mode clair</span>
              </>
            ) : (
              <>
                <Sparkles size={20} className="text-indigo-500" />
                <span className="flex-1 text-left font-medium">Mode sombre</span>
              </>
            )}
          </button>
          
          {/* Logout */}
          <button
            onClick={() => { logout(); onClose(); }}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors',
              'text-red-500 hover:bg-red-500/10'
            )}
            data-testid="mobile-logout-button"
          >
            <LogOut size={20} />
            <span className="flex-1 text-left font-medium">Déconnexion</span>
          </button>
        </div>
      </div>
    </>
  );
};

// Desktop Sidebar Component
const DesktopSidebar = ({ navigation, sidebarOpen, setSidebarOpen, currentPageInfo, user, logout, profileIcon, unreadMessages, expandedMenus, toggleMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const isItemActive = (item) => {
    if (item.href) return location.pathname === item.href;
    if (item.children) {
      return item.children.some(child => location.pathname === child.href);
    }
    return false;
  };

  return (
    <aside
      className={cn(
        'hidden md:flex border-r flex-col transition-all duration-300',
        theme === 'dark' ? 'bg-[#0A0A0A] border-white/5' : 'bg-white border-gray-200',
        sidebarOpen ? 'w-64' : 'w-20'
      )}
      data-testid="sidebar"
    >
      <div className={cn(
        'p-4 border-b flex items-center',
        theme === 'dark' ? 'border-white/5' : 'border-gray-200',
        sidebarOpen ? 'justify-center' : 'justify-center'
      )}>
        {sidebarOpen ? (
          <div className="flex items-center justify-center w-full relative">
            {/* Logo centré et très agrandi */}
            <img 
              src={theme === 'dark' ? '/logo-white.png?v=2' : '/logo.png?v=2'}
              alt="BY TRANSPORT" 
              className="h-24 w-auto"
            />
            {/* Bouton fermer en position absolue */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={cn(
                'absolute right-0 p-2 rounded-lg transition-colors',
                theme === 'dark' ? 'hover:bg-white/5 text-white' : 'hover:bg-gray-100 text-gray-700'
              )}
              data-testid="sidebar-toggle"
            >
              <X size={20} />
            </button>
          </div>
        ) : (
          /* Icône de menu quand la sidebar est fermée */
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={cn(
              'p-3 rounded-xl transition-colors',
              theme === 'dark' ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-700'
            )}
            data-testid="sidebar-toggle"
            title="Ouvrir le menu"
          >
            <Menu size={24} />
          </button>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navigation.map((item, index) => {
          const Icon = item.icon;
          const isActive = isItemActive(item);
          const isExpanded = expandedMenus[item.name];
          const showBadge = item.href === '/messages' && unreadMessages > 0;
          
          // Item with sub-menu
          if (item.isGroup && item.children && sidebarOpen) {
            return (
              <div key={item.name}>
                <button
                  onClick={() => toggleMenu(item.name)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all',
                    isActive
                      ? theme === 'dark'
                        ? 'bg-red-500/10 text-red-400'
                        : 'bg-red-50 text-red-600'
                      : theme === 'dark'
                        ? 'text-zinc-300 hover:bg-white/5 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
                  <span className="flex-1 text-left font-medium text-sm">{item.name}</span>
                  <ChevronDown 
                    size={14} 
                    className={cn(
                      'transition-transform duration-200',
                      isExpanded ? 'rotate-180' : ''
                    )} 
                  />
                </button>
                
                {/* Sub-menu items */}
                {isExpanded && (
                  <div className="ml-3 mt-1 space-y-1 border-l border-white/10 pl-3">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname === child.href;
                      const childBadge = child.href === '/messages' && unreadMessages > 0;
                      
                      return (
                        <button
                          key={child.href}
                          onClick={() => navigate(child.href)}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm',
                            childActive 
                              ? theme === 'dark'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-red-50 text-red-600'
                              : theme === 'dark'
                                ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          )}
                          data-testid={`nav-${child.href.slice(1)}`}
                        >
                          <ChildIcon size={16} strokeWidth={childActive ? 2 : 1.5} />
                          <span className="flex-1 text-left">{child.name}</span>
                          {childBadge && (
                            <span className="w-5 h-5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                              {unreadMessages > 9 ? '9+' : unreadMessages}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Collapsed sidebar - show only icons with first child route
          if (item.isGroup && item.children && !sidebarOpen) {
            const firstChild = item.children[0];
            return (
              <button
                key={item.name}
                onClick={() => navigate(firstChild.href)}
                className={cn(
                  'w-full flex items-center justify-center p-3 rounded-xl transition-all',
                  isActive 
                    ? theme === 'dark'
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-red-50 text-red-600'
                    : theme === 'dark'
                      ? 'text-zinc-300 hover:bg-white/5 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                )}
                title={item.name}
              >
                <Icon size={20} strokeWidth={isActive ? 2.5 : 1.5} />
              </button>
            );
          }
          
          // Regular item without sub-menu
          return (
            <button
              key={item.href || index}
              onClick={() => navigate(item.href)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all relative',
                isActive 
                  ? theme === 'dark'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-red-50 text-red-600 border border-red-200'
                  : theme === 'dark'
                    ? 'text-zinc-300 hover:bg-white/5 hover:text-white'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                !sidebarOpen && 'justify-center'
              )}
              data-testid={`nav-${item.href?.slice(1)}`}
            >
              <div className="relative">
                <Icon size={20} strokeWidth={isActive ? 2.5 : 1.5} />
                {showBadge && !sidebarOpen && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </div>
              {sidebarOpen && <span className="font-medium text-sm">{item.name}</span>}
              {sidebarOpen && showBadge && (
                <span className="ml-auto px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {unreadMessages}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className={cn(
        'p-4 border-t space-y-2',
        theme === 'dark' ? 'border-white/5' : 'border-gray-200'
      )}>
        {user && (
          <div className={cn(
            'flex items-center gap-3 px-2 py-2',
            !sidebarOpen && 'justify-center'
          )}>
            <ProfileAvatar user={user} profileIcon={profileIcon} size="sm" />
            {sidebarOpen && (
              <div className="text-sm overflow-hidden">
                <div className={cn(
                  'font-medium truncate',
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                )}>
                  {user.prenom} {user.nom}
                </div>
                <div className={cn(
                  'text-xs capitalize',
                  theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
                )}>
                  {user.role}
                </div>
              </div>
            )}
          </div>
        )}
        <button
          onClick={logout}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors',
            'text-red-500 hover:text-red-400 hover:bg-red-500/10',
            !sidebarOpen && 'justify-center'
          )}
          data-testid="logout-button"
        >
          <LogOut size={20} />
          {sidebarOpen && <span className="font-medium">Déconnexion</span>}
        </button>
      </div>
    </aside>
  );
};

// Mobile Header Component
const MobileHeader = ({ title, onMenuClick, onSearchClick, user, unreadMessages, unreadNotifications, profileIcon }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isConnected } = useRealTime();
  
  return (
    <header className={cn(
      'md:hidden sticky top-0 z-40 backdrop-blur-xl border-b',
      theme === 'dark' ? 'bg-[#050505]/95 border-white/10' : 'bg-white/95 border-gray-200'
    )} data-testid="mobile-header"
    style={{ paddingTop: 'max(env(safe-area-inset-top), 24px)' }}>
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        {/* Menu Button */}
        <button
          onClick={onMenuClick}
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-xl transition-colors flex-shrink-0',
            theme === 'dark' ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-700'
          )}
          data-testid="mobile-menu-button"
        >
          <Menu size={22} />
        </button>

        {/* Live Status Indicator */}
        <div 
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-full',
            isConnected 
              ? theme === 'dark' ? 'bg-green-500/20' : 'bg-green-100'
              : theme === 'dark' ? 'bg-orange-500/20' : 'bg-orange-100'
          )}
          title={isConnected ? 'Connecté' : 'Connexion perdue'}
        >
          <div className={cn(
            'w-2 h-2 rounded-full',
            isConnected ? 'bg-green-500' : 'bg-orange-500 animate-pulse'
          )} />
          <span className={cn(
            'text-[10px] font-bold uppercase',
            isConnected 
              ? theme === 'dark' ? 'text-green-400' : 'text-green-600'
              : theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
          )}>
            Live
          </span>
        </div>

        {/* Title - Centered */}
        <div className="flex-1 flex items-center justify-center min-w-0">
          <h1 className={cn(
            'text-sm font-bold uppercase tracking-wide truncate',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {title}
          </h1>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Search Button */}
          <button
            onClick={onSearchClick}
            className={cn(
              'flex items-center justify-center w-10 h-10 rounded-xl transition-colors',
              theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            )}
            data-testid="mobile-search-button"
          >
            <Search size={18} className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-600'} />
          </button>

          {/* Messages */}
          <button
            onClick={() => navigate('/messages')}
            className={cn(
              'flex items-center justify-center w-10 h-10 rounded-xl transition-colors relative',
              theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            )}
            data-testid="mobile-messages-button"
          >
            <MessageCircle size={18} className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-600'} />
            {unreadMessages > 0 && (
              <span className="absolute top-1 right-1 min-w-[14px] h-[14px] bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center px-0.5">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            )}
          </button>

          {/* Notifications */}
          <button
            onClick={() => navigate('/notifications')}
            className={cn(
              'flex items-center justify-center w-10 h-10 rounded-xl transition-colors relative',
              theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            )}
            data-testid="mobile-notifications-button"
          >
            <Bell size={18} className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-600'} />
            {unreadNotifications > 0 && (
              <span className="absolute top-1 right-1 min-w-[14px] h-[14px] bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center px-0.5">
                {unreadNotifications > 99 ? '99+' : unreadNotifications}
              </span>
            )}
          </button>

          {/* User Avatar */}
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center justify-center w-10 h-10"
            data-testid="mobile-profile-button"
          >
            <ProfileAvatar user={user} profileIcon={profileIcon} size="sm" />
          </button>
        </div>
      </div>
    </header>
  );
};

// Connection Status Banner
const ConnectionBanner = ({ connectionError, onRetry }) => {
  const { theme } = useTheme();
  
  if (!connectionError) return null;
  
  return (
    <div className={cn(
      'px-4 py-2 flex items-center justify-between text-sm',
      theme === 'dark' ? 'bg-amber-600/20 text-amber-200' : 'bg-amber-100 text-amber-800'
    )} data-testid="connection-banner">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse flex-shrink-0"></div>
        <span className="truncate">Problème de connexion au serveur</span>
      </div>
      <button 
        onClick={onRetry}
        className={cn(
          'px-3 py-1 rounded text-xs font-medium flex-shrink-0 ml-2',
          theme === 'dark' ? 'bg-amber-500/30 hover:bg-amber-500/40' : 'bg-amber-200 hover:bg-amber-300'
        )}
      >
        Réessayer
      </button>
    </div>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [profileIcon, setProfileIcon] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const { user, logout, connectionError, attemptReconnect } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Load profile icon and unread messages count
  useEffect(() => {
    const loadProfileIcon = async () => {
      try {
        const response = await api.get('/auth/profile-icon');
        setProfileIcon(response.data.icon);
      } catch (error) {
        console.error('Error loading profile icon:', error);
      }
    };
    
    const loadUnreadCount = async () => {
      try {
        const response = await api.get('/messages/unread-count');
        setUnreadMessages(response.data.unread_count || 0);
      } catch (error) {
        // Silent fail
      }
    };

    const loadUnreadNotifications = async () => {
      try {
        const response = await api.get('/notifications/unread-count');
        setUnreadNotifications(response.data.count || 0);
      } catch (error) {
        // Silent fail
      }
    };
    
    if (user) {
      loadProfileIcon();
      loadUnreadCount();
      loadUnreadNotifications();
      
      // Poll for unread messages and notifications every 10 seconds
      const interval = setInterval(() => {
        loadUnreadCount();
        loadUnreadNotifications();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Navigation with sub-menus
  const navigation = [
    { 
      name: 'Tableau de bord', 
      href: '/dashboard', 
      icon: LayoutDashboard, 
      permission: 'dashboard'
    },
    { 
      name: 'Ressources Humaines', 
      icon: Users, 
      permission: 'employees',
      isGroup: true,
      children: [
        { name: 'Salariés', href: '/employees', icon: Users, permission: 'employees' },
        { name: 'Disciplinaire', href: '/warnings', icon: FileWarning, permission: 'warnings' },
      ]
    },
    { 
      name: 'Flotte & Opérations', 
      icon: Truck, 
      permission: 'vehicles',
      isGroup: true,
      children: [
        { name: 'Véhicules', href: '/vehicles', icon: Truck, permission: 'vehicles' },
        { name: 'Maintenance', href: '/maintenance', icon: Wrench, permission: 'all' },
        { name: 'Géolocalisation', href: '/map', icon: MapPin, permission: 'map' },
        { name: 'Incidents', href: '/incidents', icon: AlertTriangle, permission: 'incidents' },
      ]
    },
    { 
      name: 'Planification', 
      icon: Calendar, 
      permission: 'planning',
      isGroup: true,
      children: [
        { name: 'Planning', href: '/planning', icon: Calendar, permission: 'planning' },
        { name: 'Tâches', href: '/tasks', icon: CheckSquare, permission: 'tasks' },
      ]
    },
    { 
      name: 'Messages', 
      href: '/messages', 
      icon: MessageCircle, 
      permission: 'messages'
    },
    { 
      name: 'Administration', 
      icon: Shield, 
      permission: 'admin',
      isGroup: true,
      children: [
        { name: 'Utilisateurs', href: '/admin', icon: Users, permission: 'admin', testId: 'nav-admin-users' },
        { name: 'Nouveau', href: '/admin/pending-users', icon: UserPlus, permission: 'admin', testId: 'nav-admin-pending' },
        { name: 'Historique', href: '/activity-log', icon: Clock, permission: 'admin', testId: 'nav-admin-history' },
      ]
    },
    { 
      name: 'Gestion Données', 
      href: '/data-management', 
      icon: Database, 
      permission: 'all',
      testId: 'nav-data-management'
    },
    { 
      name: 'Paramètres', 
      href: '/settings', 
      icon: Settings, 
      permission: 'settings'
    },
  ];

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const currentPageInfo = useMemo(() => {
    const basePath = '/' + location.pathname.split('/')[1];
    // Search in both top level and children
    for (const item of navigation) {
      if (item.href === basePath) return item;
      if (item.children) {
        const child = item.children.find(c => c.href === basePath);
        if (child) return child;
      }
    }
    return { name: 'BY TRANSPORT' };
  }, [location.pathname]);

  const userPermissions = user?.permissions || [];
  
  // Filter navigation based on permissions
  const filteredNav = navigation.map(item => {
    if (item.isGroup && item.children) {
      const filteredChildren = item.children.filter(child => {
        // 'all' permission means visible to everyone
        if (child.permission === 'all') return true;
        
        if (userPermissions.length > 0) {
          return userPermissions.includes(child.permission);
        }
        const rolePermissions = {
          admin: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'admin', 'maintenance', 'settings', 'messages'],
          rh: ['dashboard', 'employees', 'planning', 'tasks', 'incidents', 'warnings', 'settings', 'messages'],
          responsable: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'maintenance', 'settings', 'messages'],
          manager: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'maintenance', 'settings', 'messages'],
          chauffeur: ['dashboard', 'planning', 'tasks', 'incidents', 'settings', 'messages', 'vehicles', 'maintenance']
        };
        const userRole = user?.role || 'chauffeur';
        const allowedPermissions = rolePermissions[userRole] || rolePermissions.chauffeur;
        return allowedPermissions.includes(child.permission);
      });
      
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    }
    
    // Non-group items
    // 'all' permission means visible to everyone
    if (item.permission === 'all') return item;
    
    if (userPermissions.length > 0) {
      return userPermissions.includes(item.permission) ? item : null;
    }
    const rolePermissions = {
      admin: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'admin', 'maintenance', 'settings', 'messages'],
      rh: ['dashboard', 'employees', 'planning', 'tasks', 'incidents', 'warnings', 'settings', 'messages'],
      responsable: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'maintenance', 'settings', 'messages'],
      manager: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'maintenance', 'settings', 'messages'],
      chauffeur: ['dashboard', 'planning', 'tasks', 'incidents', 'settings', 'messages']
    };
    const allowedPermissions = rolePermissions[user?.role] || rolePermissions.chauffeur;
    return allowedPermissions.includes(item.permission) ? item : null;
  }).filter(Boolean);

  const currentTitle = (() => {
    for (const item of filteredNav) {
      if (item.href === location.pathname) return item.name;
      if (item.children) {
        const child = item.children.find(c => c.href === location.pathname);
        if (child) return child.name;
      }
    }
    return 'Bytransport';
  })();

  return (
    <div className={cn(
      'flex h-screen overflow-hidden',
      theme === 'dark' ? 'bg-[#050505]' : 'bg-gray-100'
    )}>
      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile Drawer Menu */}
      <MobileDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navigation={filteredNav}
        user={user}
        logout={logout}
        profileIcon={profileIcon}
        unreadMessages={unreadMessages}
        expandedMenus={expandedMenus}
        toggleMenu={toggleMenu}
      />

      {/* Desktop Sidebar */}
      <DesktopSidebar 
        navigation={filteredNav}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        currentPageInfo={currentPageInfo}
        user={user}
        logout={logout}
        profileIcon={profileIcon}
        unreadMessages={unreadMessages}
        expandedMenus={expandedMenus}
        toggleMenu={toggleMenu}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <MobileHeader 
          title={currentTitle} 
          onMenuClick={() => setMobileMenuOpen(true)}
          onSearchClick={() => setSearchOpen(true)}
          user={user}
          unreadMessages={unreadMessages}
          unreadNotifications={unreadNotifications}
          profileIcon={profileIcon}
        />

        {/* Desktop Header */}
        <header className={cn(
          'hidden md:block border-b px-8 py-4',
          theme === 'dark' ? 'bg-[#0A0A0A] border-white/5' : 'bg-white border-gray-200'
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Refresh Button */}
              <button
                onClick={() => window.location.reload()}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  theme === 'dark' 
                    ? 'hover:bg-white/10 text-zinc-400 hover:text-white' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                )}
                data-testid="desktop-refresh-button"
                title="Rafraîchir la page"
              >
                <RefreshCw size={20} />
              </button>
              <h2 className={cn(
                'text-2xl font-bold uppercase tracking-wide',
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                {currentTitle}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {/* Search Button */}
              <SearchButton onClick={() => setSearchOpen(true)} />
              
              {/* WebSocket Connection Indicator */}
              <RealTimeIndicator />
              
              <ThemeToggle />
              <button
                onClick={() => navigate('/messages')}
                className={cn(
                  'p-2 rounded-lg transition-colors relative',
                  theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                )}
                data-testid="messages-header-button"
                title="Messages"
              >
                <MessageCircle size={20} className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-600'} />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/notifications')}
                className={cn(
                  'p-2 rounded-lg transition-colors relative',
                  theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                )}
                data-testid="notifications-button"
              >
                <Bell size={20} className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-600'} />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-3">
                <ProfileAvatar user={user} profileIcon={profileIcon} size="sm" />
                <span className={cn(
                  'text-sm font-medium',
                  theme === 'dark' ? 'text-zinc-200' : 'text-gray-800'
                )}>
                  {user?.prenom} {user?.nom}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Connection Status Banner */}
        <ConnectionBanner connectionError={connectionError} onRetry={attemptReconnect} />

        {/* Main Content */}
        <main 
          className="flex-1 overflow-y-auto px-2 py-4 md:p-6 lg:p-8" 
          data-testid="main-content"
        >
          <div className="container-centered">
            {children}
          </div>
        </main>
        
        {/* Offline Banner */}
        <OfflineBanner />
      </div>
    </div>
  );
};
